// src/ui/components/brainBreak.ts — 30-Second Breathing Brain Break (FR-ADHD-002)
// Provides a structured 30-second breathing screen between learning sessions with a 15-second skip guard
// to prevent cognitive fatigue and help ADHD learners transition smoothly.

import { tokens } from '../designTokens';
import { soundEngine } from '../../audio/soundEngine';

export interface BrainBreakOptions {
  durationSeconds?: number; // default 30
  skipLockSeconds?: number; // default 15 (skip locked for first 15s)
  onComplete?: () => void;
}

export class BrainBreak {
  private container: HTMLElement;
  private durationSeconds: number;
  private skipLockSeconds: number;
  private secondsRemaining: number;
  private timerId: number | null = null;
  private onComplete?: () => void;
  private isDestroyed: boolean = false;

  constructor(options: BrainBreakOptions = {}) {
    this.durationSeconds = Math.max(5, options.durationSeconds ?? 30);
    this.skipLockSeconds = Math.min(this.durationSeconds, options.skipLockSeconds ?? 15);
    this.secondsRemaining = this.durationSeconds;
    this.onComplete = options.onComplete;

    this.container = document.createElement('div');
    this.container.id = 'brain-break-modal';
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: radial-gradient(circle at center, rgba(26, 54, 93, 0.95), rgba(10, 25, 47, 0.98));
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 2000;
      padding: 24px;
      color: #FFFDF9;
      animation: fadeIn 0.4s ease;
      touch-action: manipulation;
    `;

    this.render();
    this.startCountdown();
  }

  private startCountdown(): void {
    this.timerId = window.setInterval(() => {
      if (this.isDestroyed) return;

      this.secondsRemaining -= 1;

      if (this.secondsRemaining <= 0) {
        this.finish();
      } else {
        this.render();
      }
    }, 1000);
  }

  /** Current breathing phase based on second in 8-second cycle */
  private getBreathingPhase(): { text: string; subtext: string; scale: number } {
    const cycleSecond = (this.durationSeconds - this.secondsRemaining) % 8;
    if (cycleSecond < 3) {
      return {
        text: 'Breathe in slowly...',
        subtext: 'Draw in fresh ocean air through your nose',
        scale: 1.35,
      };
    }
    if (cycleSecond < 5) {
      return {
        text: 'Hold gently...',
        subtext: 'Keep your shoulders soft and relaxed',
        scale: 1.35,
      };
    }
    return {
      text: 'Breathe out softly...',
      subtext: 'Release like gentle underwater bubbles',
      scale: 1.0,
    };
  }

  private render(): void {
    const elapsed = this.durationSeconds - this.secondsRemaining;
    const canSkip = elapsed >= this.skipLockSeconds;
    const lockRemaining = Math.max(0, this.skipLockSeconds - elapsed);
    const phase = this.getBreathingPhase();

    this.container.innerHTML = `
      <div style="max-width: 500px; width: 100%; text-align: center; display: flex; flex-direction: column; align-items: center;">
        <!-- Header Badge -->
        <div style="display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px; border-radius: 20px; background: rgba(0, 229, 255, 0.15); border: 1.5px solid ${tokens.color.biolumCyan}; color: ${tokens.color.biolumCyan}; font-size: 0.85rem; font-weight: 800; text-transform: uppercase; margin-bottom: 24px;">
          <span>🧘</span> Naturalist Brain Break · ${this.secondsRemaining}s
        </div>

        <h2 style="font-family: ${tokens.font.heading}; font-size: 2.2rem; color: #FFFDF9; margin: 0 0 10px; line-height: 1.2;">
          ${phase.text}
        </h2>
        <p style="font-size: 1.05rem; color: rgba(255, 253, 249, 0.75); margin: 0 0 36px;">
          ${phase.subtext}
        </p>

        <!-- Calming Expanding Breathing Circle -->
        <div style="position: relative; width: 200px; height: 200px; display: flex; align-items: center; justify-content: center; margin-bottom: 40px;">
          <!-- Outer Pulsing Glow -->
          <div style="
            position: absolute;
            width: 180px;
            height: 180px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(0, 229, 255, 0.3) 0%, rgba(61, 90, 69, 0.1) 70%, transparent 100%);
            transform: scale(${phase.scale});
            transition: transform 3s ease-in-out;
          "></div>

          <!-- Main Core Breathing Orb -->
          <div style="
            position: relative;
            width: 130px;
            height: 130px;
            border-radius: 50%;
            background: linear-gradient(135deg, ${tokens.color.seaweedSage}, #1B4D3E);
            border: 3px solid ${tokens.color.gildedBrass};
            box-shadow: 0 0 28px rgba(0, 229, 255, 0.4), inset 0 0 18px rgba(255, 246, 229, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 3rem;
            transform: scale(${phase.scale});
            transition: transform 3s ease-in-out;
          ">
            🦞
          </div>
        </div>

        <!-- Controls: Skip Button with 15s Guard (FR-ADHD-002) -->
        <div style="width: 100%; max-width: 340px;">
          ${canSkip ? `
            <button
              id="btn-skip-brain-break"
              class="touch-target btn-gold"
              style="width: 100%; padding: 14px 24px; font-size: 1.1rem; border-radius: 24px;"
            >
              Ready to Continue &rarr;
            </button>
          ` : `
            <button
              id="btn-skip-brain-break"
              class="touch-target"
              disabled
              style="width: 100%; padding: 14px 24px; font-size: 1rem; border-radius: 24px; background: rgba(255, 253, 249, 0.15); color: rgba(255, 253, 249, 0.6); border: 1.5px solid rgba(255, 253, 249, 0.2); cursor: not-allowed;"
            >
              Resting your mind... (${lockRemaining}s left)
            </button>
          `}
        </div>
      </div>
    `;

    this.attachEvents();
  }

  private attachEvents(): void {
    const skipBtn = this.container.querySelector<HTMLButtonElement>('#btn-skip-brain-break');
    if (skipBtn && !skipBtn.disabled) {
      skipBtn.addEventListener('click', () => {
        soundEngine.playTap();
        this.finish();
      });
    }
  }

  /** Complete brain break, invoke callback, and remove DOM */
  finish(): void {
    if (this.isDestroyed) return;
    this.destroy();
    this.container.remove();
    this.onComplete?.();
  }

  destroy(): void {
    this.isDestroyed = true;
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  getElement(): HTMLElement {
    return this.container;
  }

  getSecondsRemaining(): number {
    return this.secondsRemaining;
  }
}
